/**
 * ReferralLandingPage — Public referral intake form
 *
 * Route: /join/:code  (no auth required)
 * Prospect fills in intake form to join Something Property via agent referral.
 * F-138
 */

import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { loadRuntimeConfig } from '../config/runtime-config';

type PageState = 'loading' | 'invalid' | 'form' | 'submitting' | 'done';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  currentAgency: string;
  monthlyDealVolume: string;
  commissionSplit: string;
  notes: string;
}

export function ReferralLandingPage({ code }: { code: string }) {
  const [state, setState] = useState<PageState>('loading');
  const [agentName, setAgentName] = useState('Something Property Agent');
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentAgency: '',
    monthlyDealVolume: '',
    commissionSplit: '',
    notes: '',
  });

  useEffect(() => {
    loadRuntimeConfig().then(cfg => {
      const base = cfg.apiGateway.baseUrl;

      // Track click
      fetch(`${base}/public/referral/${code}/click`, { method: 'POST' }).catch(() => {});

      // Validate code
      fetch(`${base}/public/referral/${code}`)
        .then(r => r.json())
        .then(json => {
          if (json.success) {
            setAgentName(json.data.agentName || 'Something Property Agent');
            setState('form');
          } else {
            setState('invalid');
          }
        })
        .catch(() => setState('invalid'));
    }).catch(() => setState('invalid'));
  }, [code]);

  const set = (key: keyof FormData, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    const required: (keyof FormData)[] = ['firstName', 'lastName', 'email', 'phone', 'currentAgency', 'monthlyDealVolume', 'commissionSplit'];
    for (const key of required) {
      if (!form[key].trim()) {
        setErrorMsg(`${key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())} is required`);
        return;
      }
    }
    if (!form.email.includes('@')) { setErrorMsg('Please enter a valid email address'); return; }

    setState('submitting');
    setErrorMsg('');

    try {
      const cfg = await loadRuntimeConfig();
      const base = cfg.apiGateway.baseUrl;
      const res = await fetch(`${base}/public/referral/${code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          monthlyDealVolume: Number(form.monthlyDealVolume) || 0,
        }),
      });
      const json = await res.json();
      if (json.success) {
        setState('done');
      } else {
        setErrorMsg(json.message || 'Submission failed. Please try again.');
        setState('form');
      }
    } catch {
      setErrorMsg('Submission failed. Please check your connection.');
      setState('form');
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-2xl shadow-2xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2 text-foreground">Invalid Referral Link</h1>
          <p className="text-muted-foreground text-sm">This referral link is not valid or has expired. Please contact the agent who shared it.</p>
        </div>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-2xl shadow-2xl p-10 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-3 text-foreground">Application Received!</h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Thank you for your interest in Something Property. We've received your application and will be in touch within 24 hours.
          </p>
          <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/20">
            <p className="text-sm text-primary font-medium">What happens next?</p>
            <p className="text-xs text-muted-foreground mt-1">Our team will review your application and reach out to schedule an onboarding call.</p>
          </div>
        </div>
      </div>
    );
  }

  const fields: { key: keyof FormData; label: string; required: boolean; type?: string; placeholder?: string }[] = [
    { key: 'firstName', label: 'First Name', required: true },
    { key: 'lastName', label: 'Last Name', required: true },
    { key: 'email', label: 'Email Address', required: true, type: 'email' },
    { key: 'phone', label: 'Phone Number', required: true, type: 'tel', placeholder: '+27 82 123 4567' },
    { key: 'currentAgency', label: 'Current Agency', required: true },
    { key: 'monthlyDealVolume', label: 'Monthly Deal Volume', required: true, type: 'number', placeholder: 'How many deals per month?' },
    { key: 'commissionSplit', label: 'Current Commission Split', required: true, placeholder: 'e.g. 70/30' },
    { key: 'notes', label: 'Message / Notes', required: false, placeholder: 'Why do you want to join Something Property?' },
  ];

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-xl mx-auto">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary/10 border border-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-primary font-bold text-xl tracking-tight">SP</span>
          </div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">Join Something Property</h1>
          <p className="text-muted-foreground text-sm mt-2">
            You were invited by <span className="text-primary font-semibold">{agentName}</span>
          </p>
        </div>

        {/* Form card */}
        <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="font-semibold text-foreground">Your Application</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Complete all fields — takes about 2 minutes</p>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {fields.slice(0, 2).map(f => (
                <div key={f.key}>
                  <Label className="text-foreground mb-1.5 block">
                    {f.label}<span className="text-destructive ml-0.5">*</span>
                  </Label>
                  <Input
                    type={f.type || 'text'}
                    value={form[f.key]}
                    onChange={e => set(f.key, e.target.value)}
                    disabled={state === 'submitting'}
                    placeholder={f.placeholder}
                  />
                </div>
              ))}
            </div>

            {fields.slice(2).map(f => (
              <div key={f.key}>
                <Label className="text-foreground mb-1.5 block">
                  {f.label}{f.required && <span className="text-destructive ml-0.5">*</span>}
                </Label>
                {f.key === 'notes' ? (
                  <textarea
                    rows={3}
                    value={form[f.key]}
                    onChange={e => set(f.key, e.target.value)}
                    disabled={state === 'submitting'}
                    placeholder={f.placeholder}
                    className="w-full rounded-md border border-border bg-input-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                ) : (
                  <Input
                    type={f.type || 'text'}
                    value={form[f.key]}
                    onChange={e => set(f.key, e.target.value)}
                    disabled={state === 'submitting'}
                    placeholder={f.placeholder}
                  />
                )}
              </div>
            ))}

            {errorMsg && (
              <div className="flex items-start gap-2 text-sm text-destructive-foreground bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2.5">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-destructive" />
                <span className="text-destructive">{errorMsg}</span>
              </div>
            )}

            <Button
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              disabled={state === 'submitting'}
              onClick={handleSubmit}
            >
              {state === 'submitting' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
              ) : (
                'Submit Application'
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              By submitting, you agree to Something Property's Terms &amp; Conditions and Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
